import { database } from '@app/firebase';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import IconClear from '@mui/icons-material/Clear';
import HomeIcon from '@mui/icons-material/Home';
import PaletteIcon from '@mui/icons-material/Palette';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import { Box, Button, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import PhotoLightBox from '@ui/components/photo-lightbox/PhotoLightBox';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import homeMetaData from '@ui/utils/metadata/homeMetaData';
import shortAddress from '@ui/utils/shortAddress';
import { makeSeoData } from 'config/next-seo.config';
import { collection, doc, getDoc, getDocs, query as firestoreQuery, where } from 'firebase/firestore';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import { NextSeo } from 'next-seo';
import React, { useState } from 'react';

import { DefaultFirestoreConverter } from '../../../../types/DefaultFirestoreConverter';
import ButtonLink from '../../../components/button-link/ButtonLink';
import { ContentBlock } from '../../../layouts/ContentBlocks';
import useStyles from './home.style';


export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  let homeData: guesthouse.Home | null = null;

  let queryHomeId: string;

  if (typeof query.home === 'object') {
    queryHomeId = query.home[0];
  } else {
    queryHomeId = query.home;
  }

  const home = await getDoc<guesthouse.Home>(doc(database, `homes/${queryHomeId}`).withConverter(new DefaultFirestoreConverter<guesthouse.Home>()));

  homeData = home.data();

  if (!homeData?.docID) {
    const homeDocs = await getDocs(firestoreQuery(collection(database, 'homes').withConverter(new DefaultFirestoreConverter<guesthouse.Home>()),
      where('slug', '==', query.home)));

    homeData = homeDocs?.docs?.length && homeDocs.docs[0].data();
  }

  if (!homeData?.docID || !homeData?.case_study) {
    return {
      notFound: true,
    };
  }

  let roomsData = [];

  if (homeData.docID) {
    const homeDoc = doc(collection(database, 'homes'), homeData.docID);
    const homeRoomsCollection = collection(homeDoc, 'rooms').withConverter(new DefaultFirestoreConverter<guesthouse.Room>());
    const rooms = await getDocs<guesthouse.Room>(firestoreQuery(homeRoomsCollection, where('published', '==', true)));

    roomsData = rooms?.docs.map(r => r.data());
  }

  // Fetch photos for the home
  let backgroundPhoto: guesthouse.Photo | null = null;
  let allPhotos: guesthouse.Photo[] = [];

  if (homeData.docID) {
    const photoDocs = await getDocs(firestoreQuery(
      collection(database, `homes/${homeData.docID}/photos`),
      where('status', '==', 'COMPLETE')
    ));

    allPhotos = photoDocs.docs.map(doc => doc.data() as guesthouse.Photo);
    
    // Get a photo that's not the primary photo, or just get the second one
    const nonPrimaryPhotos = allPhotos.filter(photo => 
      photo.docID !== homeData.primaryPhoto?.docID
    );
    
    backgroundPhoto = nonPrimaryPhotos.length > 0 
      ? nonPrimaryPhotos[0] 
      : allPhotos.length > 1 
        ? allPhotos[1] 
        : allPhotos[0] || null;
  }

  const props = {
    home: JSON.stringify(homeData),
    rooms: JSON.stringify(roomsData),
    metadata: JSON.stringify(homeMetaData(homeData)),
    backgroundPhoto: backgroundPhoto ? JSON.stringify(backgroundPhoto) : null,
    photos: JSON.stringify(allPhotos),
  };

  return { props };
};

function SuccessHomePage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { md } = useBreakpoints();
  const { theme, classes } = useStyles();
  const home = JSON.parse(props.home) as guesthouse.Home;
  const metadata = JSON.parse(props.metadata) as guesthouse.MetaData;


  const backgroundPhoto = props.backgroundPhoto 
    ? (JSON.parse(props.backgroundPhoto) as guesthouse.Photo)
    : null;
  const allPhotos = props.photos 
    ? (JSON.parse(props.photos) as guesthouse.Photo[])
    : [];
  
  // Filter to only show photos with case_study flag, excluding primary and background photos
  const gridPhotos = allPhotos.filter(photo => 
    photo.case_study === true &&
    photo.docID !== home.primaryPhoto?.docID && 
    photo.docID !== backgroundPhoto?.docID
  );

  const [selectedPhotoID, setSelectedPhotoID] = useState<string | undefined>();
  const [lightboxOpen, setLightboxOpen] = useState(false);

  //if home does not have case_study set to true return 404
  if (!home.case_study) {
    return {
      notFound: true,
    };
  }

  return (
    <>
      <Head>
        <title>
          {metadata.pageTitle}
        </title>

        {!home.published && (
        <meta
          name="robots"
          content="noindex"
        />
        )}
      </Head>

      <NextSeo {...makeSeoData(metadata)} />
      {home.case_study_title && (
        <ContentBlock
          maxWidth={900}
          margin={[0, 'auto']}
          padding={md ? [12, 0] : [6, 2]}
        >
          <Typography
            variant="h1"
            style={{ fontSize: md ? 32 : 24, textAlign: 'center' }}
          >
            {home.case_study_title || 'Case Study'}
          </Typography>
        </ContentBlock>
      )}
      
      <ContentBlock
        maxWidth={1400}
        margin={[0, 'auto']}
        padding={md ? [0] : [0, 2]}
      >
        <Box
          display="flex"
          flexDirection={md ? 'row' : 'column'}
          gap={md ? 8 : 2}
        >
          <Box
            flex={md ? 3 : undefined}
            sx={{
              backgroundColor: '#f0f0f0',
              padding: '12px',
              position: 'relative',
              height: md ? '600px' : '300px',
              width: md ? undefined : '100%',
            }}
          >
            {home.primaryPhoto?.large_url ? (
              <Image
                fill
                src={home.primaryPhoto.large_url}
                alt={home.case_study_title || 'Case study home'}
                style={{ objectFit: 'cover', width: '100%', height: '100%', objectPosition: 'top center' }}
              />
            ) : (
              <Image
                fill
                src={'/images/homepage/header.png'}
                alt="Hero image"
                style={{ objectFit: 'cover', width: '100%', height: '100%', objectPosition: 'top center' }}
              />
            )}
          </Box>
          <Box
            flex={md ? 2 : undefined}
            className={classes.caseStudyInfo}
            width={md ? undefined : '100%'}
          >
            <Typography
              variant="h2"
              style={{ fontSize: md ? undefined : 20 }}
            >
              {shortAddress(home.address)}
            </Typography>
            {home.case_study_the_home && (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              className={classes.caseStudyInfoItem}
            >
              <HomeIcon />
              <Typography variant="body1">
                <b>
                  The home:
                </b>
                {' '}
                {home.case_study_the_home || 'The home'}
              </Typography>
            </Box>
            )}
            {home.case_study_listed_by && (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              className={classes.caseStudyInfoItem}
            >
              <PersonIcon />
              <Typography variant="body1">
                <b>
                  Listed by:
                </b>
                {' '}
                {home.case_study_listed_by || 'Listed by'}
              </Typography>
            </Box>
            )}
            {home.case_study_services_booked && (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              className={classes.caseStudyInfoItem}
            >
              <PaletteIcon />
              <Typography variant="body1">
                <b>
                  Services booked:
                </b>
                {' '}
                {home.case_study_services_booked}
              </Typography>
            </Box>
            )}
            {home.case_study_vibe && (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              className={classes.caseStudyInfoItem}
            >
              <StarIcon />
              <Typography variant="body1">
                <b>
                  Vibe:
                </b>
                {' '}
                {home.case_study_vibe || 'Vibe'}
              </Typography>
            </Box>
            )}
            {home.case_study_staging_turnaround && (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              className={classes.caseStudyInfoItem}
            >
              <AccessTimeIcon />
              <Typography variant="body1">
                <b>
                  Staging turnaround:
                </b>
                {' '}
                {home.case_study_staging_turnaround || 'Staging turnaround'}
              </Typography>
            </Box>
            )}
          </Box>
        </Box>
      </ContentBlock>

            
      <ContentBlock
        margin={[0, 'auto']}
        padding={md ? [6, 0] : [2, 0]}
      >

        {home.case_study_main_cta_text && (
          <ContentBlock
            maxWidth={900}
            margin={[0, 'auto']}
            padding={md ? [6, 0] : [4, 2]}
          >
            <Typography
              variant="h2"
              style={{ fontSize: md ? 36 : 18, textAlign: 'center' }}
            >
              {home.case_study_main_cta_text}
            </Typography>
          </ContentBlock>
        )}
        {home.case_study_description && (
          <ContentBlock
            maxWidth={1200}
            margin={[0, 'auto']}
            padding={md ? [0, 0] : [0, 2]}
          >
            <Typography
              variant="body1"
              style={{ fontSize: md ? 20 : 16, lineHeight: 1.6 }}
            >
              {home.case_study_description}
            </Typography>
          </ContentBlock>
        )}
        {gridPhotos.length > 0 && (
          <ContentBlock
            maxWidth={1400}
            margin={[0, 'auto']}
            padding={md ? [12, 0] : [6, 2]}
          >
            <div className={classes.photoGrid}>
              {gridPhotos.map((photo) => (
                <div
                  key={photo.docID}
                  className={classes.photoGridItem}
                  style={{ 
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setSelectedPhotoID(photo.docID);
                    setLightboxOpen(true);
                  }}
                >
                  <Image
                    fill
                    src={photo.medium_url || photo.large_url}
                    alt=""
                    sizes="(min-width: 1200px) 25vw, (min-width: 900px) 33vw, 50vw"
                    style={{
                      objectFit: 'cover',
                    }}
                  />
                </div>
              ))}
            </div>
          </ContentBlock>
        )}
        
        {home.case_study_testimonial_text && (
          <ContentBlock
            maxWidth={1200}
            margin={[0, 'auto']}
            padding={md ? [6, 0] : [0, 0]}
          >
            <Box className={classes.testimonialContainer}>
              <Typography
                variant="h3"
                className={classes.testimonialQuote}
              >
                &ldquo;
                {home.case_study_testimonial_text}
                &rdquo;
              </Typography>
              <Typography
                variant="body1"
                className={classes.testimonialAuthor}
              >
                &mdash;
                {' '}
                {home.case_study_testimonial_author}
              </Typography>
            </Box>
          </ContentBlock>
        )}
      </ContentBlock>

      <ContentBlock
        margin={[0]}
        width={'100vw'}
      >
        <div
          style={{     
            width: '100vw',
            position: 'relative',
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: 'none', 
            height: md ? 300 : 200, 
            overflow: 'hidden',  
            borderRadius: 0, 
            marginTop: 0, 
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 2,
            }}
          >

          </div>
          <Image
            fill
            src={backgroundPhoto?.large_url || '/images/homepage/custom-design.png'}
            alt=""
            sizes="100vw"
            style={{
              objectFit: 'cover',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1,
            }}
          />
          <Box
            sx={{ margin: 'auto',
              textAlign: 'center',
              position: 'relative',
              color: theme.palette.common.white,
              zIndex: 3,
              padding: md ? 0 : 2,
            }}
          >
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="h2"
              component="h3"
              style={{ marginBottom: md ? theme.spacing(3) : theme.spacing(3), fontSize: md ? 48 : 20 }}
            >
              Want results like this?
            </Typography>

            <Button
              variant="contained"
              color="white"
              component={ButtonLink}
              href="/home-staging/get-a-quote"
              style={{
                display: 'inline-block',
                fontWeight: 'bold',
                backgroundColor: '#ffffff',
                color: '#080808',
                textDecoration: 'none !important',
                borderRadius: '30px',
                padding: '16px 24px',
                lineHeight: 1.4,
                height: 'auto',
                marginRight: '16px',
                border: 'none',
                fontSize: md ? 18 : 14,
                marginBottom: md ? 0 : 16,
              }}
            >
              Get an instant quote
            </Button>
            <Button
              variant="contained"
              color="white"
              component={ButtonLink}
              href="https://guesthouseshop-8563089.hs-sites-na2.com/book-now"
              target="_blank"
              style={{
                display: 'inline-block',
                fontWeight: 'bold',
                backgroundColor: '#080808',
                color: '#ffffff',
                textDecoration: 'none !important',
                borderRadius: '30px',
                padding: '16px 24px',
                lineHeight: 1.4,
                height: 'auto',
                marginRight: '16px',
                border: 'none',
                fontSize: md ? 18 : 16,
              }}
            >
              Talk with our team
            </Button>
        
          </Box>
        </div>
      </ContentBlock>

      <Dialog
        fullScreen
        open={lightboxOpen}
        maxWidth={false}
        style={{
          height: '100%',
        }}
        PaperProps={{
          style: {
            height: '100%',
            backgroundColor: 'red',
          }
        }}
        onClose={() => setLightboxOpen(false)}
      >
        <PhotoLightBox
          photos={gridPhotos}
          selectedPhotoID={selectedPhotoID}
          setSelectedPhotoID={setSelectedPhotoID}
          renderImage={({ photo }) => {
            if (photo) {
              return (
                <img
                  src={photo.large_url}
                  alt=""
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'contain',
                  }}
                />
              );
            }
            return null;
          }}
          renderInteractions={() => {
            return (
              <Box
                style={{
                  position: 'absolute',
                  top: theme.spacing(0),
                  right: theme.spacing(4),
                  zIndex: 10,
                }}
              >
                <Button
                  variant="text"
                  color="inherit"
                  size="large"
                  startIcon={<IconClear />}
                  style={{
                    color: 'white',
                  }}
                  onClick={() => setLightboxOpen(false)}
                >
                  Close
                </Button>
              </Box>
            );
          }}
        />
      </Dialog>
    </>
  );
}

export default SuccessHomePage;
